import React from "react";
import { HeaderBar, FilesBlock, TableBlock } from "./Blocks";

/** kind -> 렌더러 매핑 */
const FACTORY = {
  title: ({
    meta,
    alignMode,
    value,
    onChange,
    onRename,
    onToggleLock,
    onRemove,
  }) => (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col h-full">
      <HeaderBar
        id="block-title"
        meta={meta}
        alignMode={alignMode}
        onRename={onRename}
        onToggleLock={onToggleLock}
        onRemove={onRemove}
      />
      <div className="p-3">
        <input
          type="text"
          placeholder="제목을 입력하세요"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={alignMode}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 outline-none disabled:bg-slate-50 no-drag"
        />
      </div>
    </div>
  ),

  date: ({
    meta,
    alignMode,
    value,
    todayISO,
    onChange,
    onRename,
    onToggleLock,
    onRemove,
  }) => (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col h-full">
      <HeaderBar
        id="block-date"
        meta={meta}
        alignMode={alignMode}
        onRename={onRename}
        onToggleLock={onToggleLock}
        onRemove={onRemove}
      />
      <div className="p-3">
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value || todayISO())}
          disabled={alignMode}
          className="px-3 py-2 rounded-lg border border-slate-200 outline-none disabled:bg-slate-50 no-drag"
        />
      </div>
    </div>
  ),

  editor: ({
    meta,
    alignMode,
    Editor,
    editorProps,
    onRename,
    onToggleLock,
    onRemove,
    hiddenFileRef,
  }) => (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col h-full">
      <HeaderBar
        id="block-editor"
        meta={meta}
        alignMode={alignMode}
        onRename={onRename}
        onToggleLock={onToggleLock}
        onRemove={onRemove}
      />
      <div className="p-3 flex-1 min-h-0">
        <Editor {...editorProps} />
        <input
          ref={hiddenFileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={() => {}}
        />
      </div>
    </div>
  ),

  photos: ({
    meta,
    alignMode,
    onRename,
    onToggleLock,
    onRemove,
    inputProps,
    list,
    onInsert,
    onRemovePhoto,
  }) => (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col h-full">
      <HeaderBar
        id="block-photos"
        meta={meta}
        alignMode={alignMode}
        onRename={onRename}
        onToggleLock={onToggleLock}
        onRemove={onRemove}
      />
      <div className="p-3">
        <div className="flex items-center gap-2">
          <input
            type="file"
            accept="image/*"
            multiple
            {...inputProps}
            disabled={alignMode}
            className="disabled:opacity-50 no-drag"
          />
          <span className="text-xs text-slate-500">여러 장 선택 가능</span>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2">
          {list.map((p) => (
            <div
              key={p.id}
              className="group relative border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm"
            >
              <div
                className="flex items-center justify-center overflow-hidden bg-slate-100"
                style={{ aspectRatio: "16 / 9" }}
              >
                <img
                  src={p.src}
                  alt={p.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-2 flex items-center justify-between gap-2">
                  <div className="text-xs text-white/95 truncate">{p.name}</div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => onInsert(p)}
                      className="pointer-events-auto px-2 py-1 text-xs rounded-md border border-white/60 bg-white/90 hover:bg-white no-drag"
                    >
                      삽입
                    </button>
                    <button
                      type="button"
                      onClick={() => onRemovePhoto(p.id)}
                      className="pointer-events-auto px-2 py-1 text-xs rounded-md border border-white/60 bg-white/90 hover:bg-white no-drag"
                    >
                      삭제
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {list.length === 0 && (
            <div className="text-xs text-slate-400 col-span-full">
              아직 추가된 사진이 없습니다.
            </div>
          )}
        </div>
      </div>
    </div>
  ),

  note: ({
    meta,
    alignMode,
    value,
    onChange,
    onRename,
    onToggleLock,
    onRemove,
  }) => (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col h-full">
      <HeaderBar
        id={meta?.id || "block-note"}
        meta={meta}
        alignMode={alignMode}
        onRename={onRename}
        onToggleLock={onToggleLock}
        onRemove={onRemove}
      />
      <div className="p-3 flex-1 min-h-0">
        <textarea
          placeholder="자유 메모"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={alignMode}
          className="w-full h-full min-h-40 resize-none outline-none disabled:bg-slate-50 no-drag bg-white rounded-lg ring-1 ring-inset ring-slate-200 p-3"
        />
      </div>
    </div>
  ),

  files: ({
    meta,
    alignMode,
    onRename,
    onToggleLock,
    onRemove,
    files,
    onAttach,
    onRemoveFile,
  }) => (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col h-full">
      <HeaderBar
        id="block-files"
        meta={meta}
        alignMode={alignMode}
        onRename={onRename}
        onToggleLock={onToggleLock}
        onRemove={onRemove}
      />
      <FilesBlock
        files={files}
        onAttach={onAttach}
        onRemove={onRemoveFile}
        alignMode={alignMode}
      />
    </div>
  ),

  table: ({
    meta,
    alignMode,
    bid,
    t,
    onAddRow,
    onRemoveRow,
    onUpdateCell,
    onRename,
    onToggleLock,
    onRemove,
  }) => (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col h-full">
      <HeaderBar
        id={bid}
        meta={meta}
        alignMode={alignMode}
        onRename={onRename}
        onToggleLock={onToggleLock}
        onRemove={onRemove}
      />
      <TableBlock
        bid={bid}
        t={t}
        alignMode={alignMode}
        onAddRow={onAddRow}
        onRemoveRow={onRemoveRow}
        onUpdateCell={onUpdateCell}
      />
    </div>
  ),
};

export default function BlockRenderer({ kind, ...props }) {
  const Comp = FACTORY[kind];
  return Comp ? Comp(props) : null;
}
